import AddFavModal from "@shared/AddFavModal";
import AddCompaniesToWorkspaceModal from "../../workspace/components/AddCompaniesToWorkspaceModal";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { useEffect, useState, useMemo } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { getApiUrl } from "@utils/baseUrl";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { Info, X } from "react-feather";
import { ERROR_MSG } from "@utils/enum";
import { getRemainingValueAddCredits } from "@core/utility/utility";
import { useSelector } from "react-redux";
import ContactModal from "@shared/country-components/contactModal";
import FeatureNudge from "@shared/country-components/FeatureNudge";
import DynamicDropdown from "@components/base/DynamicDropDown/DynamicDropDown";
import { captureException } from "@sentry/browser";
import moment from "moment";
import { companySearchTabs } from "../enum";

const DOWNLOAD_LIMIT = 500;
const CREDITS_PER_DOWNLOAD = 5;

const CompanyResultHeaders = ({ selectedRows, setSelectedRows, totalDataCount = 0, clearSelections, singleRow, setSingleRow, filters, searchedState, sortField, activeTab, tableData, source, onDownloadOverride }) => {
  const [showFavModal, setShowFavModal] = useState(false);
  const [showAddWorkspaceModal, setShowAddWorkspaceModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [downloadFromValue, setDownloadFromValue] = useState(1);
  const [downloadToValue, setDownloadToValue] = useState(totalDataCount);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFromModal, setDownloadFromModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showContactsDrop, setShowContactsDrop] = useState(false);

  const userDetails = useSelector((state) => state.userDetails);
  const creditDetails = userDetails?.creditDetails?.credit_details;
  const userFirstPlan = userDetails?.creditDetails?.data?.[0] ?? null;
  const remainingValueAddCount = getRemainingValueAddCredits(userFirstPlan ?? userDetails?.creditDetails?.credit_details);
  const currentUserPlan = userFirstPlan?.user_active_plan;

  const STARTERPLAN = creditDetails?.data?.find((data) => data?.is_active && data?.user_active_plan?.toLowerCase()?.includes("starter"))?.navigate === "search";

  const selectedRowsCount = useMemo(() => selectedRows?.length ?? 0, [selectedRows]);
  // Company search and bulk search results carry no trade direction, so the user picks the prospect type.
  const isCompanySearchSource = source === "company_tab" || source === "company_bulk_search";
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const country = useMemo(() => urlParams.get("country"), [urlParams]);
  const type = useMemo(() => urlParams.get("type"), [urlParams]);
  const downloadCount = useMemo(() => (downloadFromModal ? downloadToValue - downloadFromValue + 1 : selectedRowsCount), [downloadFromModal, downloadToValue, downloadFromValue, selectedRowsCount]);
  const creditCost = useMemo(() => downloadCount * CREDITS_PER_DOWNLOAD, [downloadCount]);
  const workspaceCreationFilters = useMemo(() => {
    if (!filters || !searchedState) return null;
    const companyType = searchedState?.tradeDirection?.value === "import" ? "consignee" : "shipper";
    const isProductTab = activeTab === companySearchTabs.PRODUCT;
    const payload = {
      ...(isProductTab ? { tab: "company" } : { search_by: "company" }),
      isFilterDataIncludes: false,
      data_type: searchedState?.datasetType || "",
      company_tab_trade_direction: "both",
      from: 0,
      sort: sortField?.value || "date",
      search_type: "basic",
      order: "desc",
      companyType,
      fromDate: filters?.dateRange?.startDate || "",
      toDate: filters?.dateRange?.endDate || "",
      search: Array.isArray(searchedState?.search) ? searchedState.search.map((item) => item?.label || "").join("||") : "",
      key: searchedState?.search?.[0]?.category === "HS Code" ? "product_hscode" : searchedState?.search?.[0]?.category === "Product" ? "product_description" : "product_and_hscode",
      queryType: "exact",
      must_not: [],
      uniqueImportExporterShipment: filters?.metrics?.NumberofShipments?.from && filters?.metrics?.NumberofShipments?.to ? [+filters.metrics.NumberofShipments.from, +filters.metrics.NumberofShipments.to] : [],
      uniqueImportExporterWeight: filters?.metrics?.TotalWeight?.from && filters?.metrics?.TotalWeight?.to ? [+filters.metrics.TotalWeight.from, +filters.metrics.TotalWeight.to] : [],
      uniqueImportExporterValue: filters?.metrics?.TotalTradeValue?.from && filters?.metrics?.TotalTradeValue?.to ? [+filters.metrics.TotalTradeValue.from, +filters.metrics.TotalTradeValue.to] : [],
    };
    if (isProductTab && filters?.jurisdiction?.length > 0) payload[`${companyType}JurisdictionCountry`] = filters.jurisdiction.map((item) => item.toLowerCase());
    if (isProductTab && filters?.hsCode?.length > 0) payload.sixDigitHsCode = filters.hsCode;
    return payload;
  }, [activeTab, filters, searchedState, sortField]);

  const contactsReqTypes = useMemo(
    () => [
      {
        name: "Employee Level Contacts",
        cb: () => {
          const maxRequest = userFirstPlan?.contact_max_request ?? 0;
          const currentRequest = userFirstPlan?.contact_current_request ?? 0;
          if (selectedRowsCount <= maxRequest - currentRequest) {
            setShowContactModal(true);
          } else {
            const displayMessage = `You can request contacts for ${maxRequest} companies in a day. You can request for ${maxRequest - currentRequest} more companies as you have already requested contacts for ${currentRequest} companies today.`;
            toast.warning(displayMessage);
          }
        },
      },
      {
        name: "Extracted Contacts",
        cb: async () => {
          try {
            const company_ids = [];
            selectedRows.forEach((item) => {
              if (item && item.key && (item.shipper_company_id || item.consignee_company_id || item.hashed_id)) {
                company_ids.push(item.hashed_id ?? item.shipper_company_id ?? item.consignee_company_id);
              }
            });
            const { data, status } = await axios.post(getApiUrl("CompanyContactDetails/fetchBulkAIContacts"), { company_ids });
            if (data?.message && status === 200) {
              toast.success(data.message);
            } else {
              toast.error(data.message);
            }
          } catch (error) {
            toast.error("Failed to fetch AI contact");
            captureException(error);
          }
        },
      },
    ],
    [selectedRowsCount, selectedRows, userFirstPlan]
  );

  const availableCredits = useMemo(() => {
    if (!creditDetails?.download_credits) return 0;
    return creditDetails.download_credits.credit_limit - creditDetails.download_credits.credits_consumed;
  }, [creditDetails]);

  const isEmailShipments = useMemo(() => {
    if (!downloadFromValue || !downloadToValue) return false;
    const count = downloadToValue - downloadFromValue + 1;
    return count >= DOWNLOAD_LIMIT;
  }, [downloadFromValue, downloadToValue]);

  const isDownloadRangeValid = useMemo(() => {
    const count = downloadToValue - downloadFromValue + 1;
    return count > 0 && count <= 10000;
  }, [downloadFromValue, downloadToValue]);

  useEffect(() => {
    setDownloadToValue(totalDataCount > 10000 ? 10000 : totalDataCount);
  }, [totalDataCount]);

  useEffect(() => {
    if (singleRow) {
      setShowFavModal(true);
    }
  }, [singleRow]);

  const createExcelImporterExporter = async () => {
    if (onDownloadOverride) {
      try {
        await onDownloadOverride(selectedRows);
        setDownloadFromModal(false);
        setShowDownloadModal(false);
        setShowConfirmModal(false);
      } catch (error) {
        captureException(error);
        toast.error(error instanceof Error && error.message ? error.message : ERROR_MSG);
      }
      return;
    }

    try {
      const file_name = "UniqueImporters_" + moment().format("DD-MM-YYYY") + "_" + new Date().getTime() + ".xlsx";
      let payload = { file_name };

      if (selectedRowsCount > 0) {
        payload = {
          ...payload,
          data_download: selectedRows,
          _payload: {
            companyType: searchedState?.tradeDirection?.value === "import" ? "consignee" : "shipper",
            fromDate: filters.dateRange.startDate,
            toDate: filters.dateRange.endDate,
            search: searchedState.search.map((item) => item.label).join("||"),
            key: searchedState.search[0].category === "HS Code" ? "product_hscode" : searchedState.search[0].category === "Product" ? "product_description" : "product_and_hscode",
            queryType: "exact",
            ...(activeTab === companySearchTabs.PRODUCT ? { tab: "company" } : { search_by: "company" }),
          },
          is_email: selectedRowsCount > DOWNLOAD_LIMIT,
        };
      } else {
        const selectedDataCount = downloadToValue - downloadFromValue + 1;
        payload = {
          ...payload,
          tab: "company",
          data_type: searchedState.datasetType,
          company_tab_trade_direction: "both",
          sort: sortField.value,
          search_type: "basic",
          order: "desc",
          companyType: searchedState?.tradeDirection?.value === "import" ? "consignee" : "shipper",
          fromDate: filters.dateRange.startDate,
          toDate: filters.dateRange.endDate,
          search: searchedState.search.map((item) => item.label).join("||"),
          key: searchedState.search[0].category === "HS Code" ? "product_hscode" : searchedState.search[0].category === "Product" ? "product_description" : "product_and_hscode",
          queryType: "exact",
          uniqueImportExporterShipment: filters.metrics.NumberofShipments.from && filters.metrics.NumberofShipments.to ? [+filters.metrics.NumberofShipments.from, +filters.metrics.NumberofShipments.to] : [],
          uniqueImportExporterWeight: filters.metrics.TotalWeight.from && filters.metrics.TotalWeight.to ? [+filters.metrics.TotalWeight.from, +filters.metrics.TotalWeight.to] : [],
          uniqueImportExporterValue: filters.metrics.TotalTradeValue.from && filters.metrics.TotalTradeValue.to ? [+filters.metrics.TotalTradeValue.from, +filters.metrics.TotalTradeValue.to] : [],
          trendType: "year",
          email: selectedDataCount > DOWNLOAD_LIMIT,
          from: downloadFromValue - 1,
          size: selectedDataCount,
        };
        if (filters.jurisdiction?.length > 0) payload[`${payload.companyType}JurisdictionCountry`] = filters.jurisdiction.map((item) => item.toLowerCase());
      }
      const { data } = await axios.post(getApiUrl("create_excel_multi_country"), payload);

      if (data?.status === "success") {
        trademoTrackingEvents(trackingEventDictionary.companySearchDownload, generateTrackingObject("company-search", window.location.pathname, currentUserPlan));
        setDownloadFromModal(false);
        setShowDownloadModal(false);
        setShowConfirmModal(false);
        if (data?.file_url) window.open(data.file_url, "_blank");
        if (data?.message) toast.success(data.message);
      } else {
        // a failed download can still come back with a 200, so the catch below never sees it
        toast.error(data?.message || ERROR_MSG);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || ERROR_MSG);
      captureException(error);
    }
  };

  const handleDownloadFromChange = (value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue <= 0) {
      setDownloadFromValue(1);
    } else {
      setDownloadFromValue(Math.min(numValue, totalDataCount));
    }
  };

  const handleDownloadToChange = (value) => {
    const numValue = parseInt(value, 10);
    setDownloadToValue(Math.min(numValue, totalDataCount));
  };

  const showImporterConfirmModal = () => {
    if (downloadToValue < downloadFromValue) {
      toast.error("TO value must be greater than FROM value");
      return;
    }

    if (creditCost > availableCredits) {
      setShowDownloadModal(false);
      toast.error(`You do not have sufficient credits available to download ${downloadCount} ${type}.`);
      return;
    }

    setShowDownloadModal(false);
    setDownloadFromModal(true);
    setShowConfirmModal(true);
  };

  const handleDownloadClick = () => {
    if (selectedRowsCount > 0) {
      setShowConfirmModal(true);
      setDownloadFromModal(false);
    } else {
      setDownloadFromModal(true);
      setShowDownloadModal(true);
    }
  };

  const handleCloseFavModal = () => {
    setShowFavModal(false);
    setSingleRow(null);
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
  };

  const handleCompanySelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(tableData);
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-2 tw-text-gray-900 tw-divide-x tw-divide-gray-200">
        {activeTab === companySearchTabs.PRODUCT && selectedRowsCount > 0 && (
          <div className="tw-text-primary-purple-500 tw-font-semibold tw-text-xs">
            <span className="tw-text-xs tw-font-semibold tw-text-primary-purple-600 tw-bg-primary-purple-100 tw-rounded-xl tw-px-2 tw-py-0.5 tw-mr-1">{selectedRowsCount}</span>
            {" rows selected "}
            <span className="tw-cursor-pointer" onClick={clearSelections}>
              <X className="tw-h-4" />
            </span>
          </div>
        )}
        {activeTab === companySearchTabs.COMPANY && (
          <div className="tw-text-gray-900 tw-font-normal tw-text-sm tw-flex tw-items-center tw-gap-2">
            <input type="checkbox" checked={tableData.length === (selectedRows?.length || 0)} disabled={tableData.length === 0} onChange={(e) => handleCompanySelectAll(e)} className="tw-h-4 tw-w-4 tw-rounded tw-border-gray-200 tw-accent-primary-purple-600 tw-cursor-pointer" />
            Select All
          </div>
        )}

        <div className={`tw-border-gray-200 tw-flex tw-items-center tw-gap-2 tw-h-8 ${selectedRowsCount || activeTab === companySearchTabs.COMPANY ? "tw-pl-2 tw-border-l" : ""}`}>
          {(selectedRowsCount || activeTab === companySearchTabs.PRODUCT) && (
            <div onClick={handleDownloadClick} className="tw-border tw-border-[#e7e7e7] tw-bg-white tw-w-fit tw-px-3 tw-py-1.5 tw-rounded-lg tw-shadow-button hover:tw-bg-gray-25 tw-cursor-pointer tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold">
              <span className="material-symbols-outlined">cloud_download</span>
              &nbsp;Download
            </div>
          )}

          {selectedRowsCount > 0 && (
            <div onClick={() => setShowFavModal(true)} className="tw-border tw-border-[#e7e7e7] tw-bg-white tw-w-fit tw-px-3 tw-py-1.5 tw-rounded-lg tw-shadow-button hover:tw-bg-gray-25 tw-cursor-pointer tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-h-8">
              <span className="material-symbols-outlined">star</span>
              &nbsp;Bookmark
            </div>
          )}

          {selectedRowsCount > 0 && (
            <div id="add_to_workspace_nudge" onClick={() => setShowAddWorkspaceModal(true)} className="tw-border tw-border-[#e7e7e7] tw-bg-white tw-w-fit tw-px-3 tw-py-1.5 tw-rounded-lg tw-shadow-button hover:tw-bg-gray-25 tw-cursor-pointer tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-h-8">
              <span className="material-symbols-outlined">folder_open</span>
              &nbsp;Add to Workspace
            </div>
          )}

          {!STARTERPLAN && (
            <>
              <DynamicDropdown
                isOpen={showContactsDrop}
                onToggle={() => {
                  if (userFirstPlan.view_email_details_max_allowed - selectedRowsCount < 0 && userFirstPlan.view_phone_details_max_allowed - selectedRowsCount < 0) {
                    return toast.error("You do not have enough credits to fetch the contacts. Please reach out to customersuccess@trademo.com for more credits.");
                  }
                  setShowContactsDrop(!showContactsDrop);
                }}
                onClose={() => setShowContactsDrop(false)}
                icon={{
                  before: true,
                  onOpen: "contacts",
                  onClose: "contacts",
                }}
                buttonLabel={"Fetch Contacts"}
                options={contactsReqTypes}
                buttonId="fetch-contacts-btn"
                disabled={(selectedRowsCount === 0 || selectedRowsCount > 100) && !STARTERPLAN}
                tooltipConfig={{
                  id: "fetchcontact",
                  message: STARTERPLAN ? "Upgrade your plan to fetch contacts." : selectedRowsCount === 0 ? "To perform this action, you need to select records from the table." : selectedRowsCount > 100 ? "Please select upto 100 companies" : "Request contacts for selected companies",
                }}
                buttonClassName="tw-ml-1 tw-mr-2 tw-h-8 tw-font-semibold tw-text-xs"
                dropdownClassName="tw-shadow-sideDrawer tw-border-gray-300 tw-min-w-[220px] tw-left-1"
              />
              <ReactTooltip id="fetchcontact" className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="bottom" />
            </>
          )}
        </div>
      </div>

      {/* Introducing Workspace nudge — anchors to the Add to Workspace button (shown when there is a selection).
          Shown once per module: this component backs both Product Search and Company Search, so the key is
          chosen from `source` to track/dismiss each independently. */}
      <FeatureNudge nudgeKey={source === "product_search" ? "workspace_product_search" : "workspace_company_search"} enabled={selectedRowsCount > 0} />

      {/* Favorites Modal */}
      {showFavModal && <AddFavModal profileType="company" show={showFavModal} handleClose={handleCloseFavModal} prospectData={singleRow || selectedRows} tradeDirection={type} dataSource={country} onAddSuccess={() => trademoTrackingEvents(trackingEventDictionary.companySearchAddToFavouritesConfirm, generateTrackingObject("company-search", window.location.pathname, currentUserPlan))} />}

      {showAddWorkspaceModal && (
        <AddCompaniesToWorkspaceModal
          show={showAddWorkspaceModal}
          onHide={() => setShowAddWorkspaceModal(false)}
          companies={selectedRows}
          onSuccess={() => {
            setShowAddWorkspaceModal(false);
            clearSelections?.();
          }}
          type={searchedState?.tradeDirection?.value === "import" ? "buyer" : "supplier"}
          selectProspectType={isCompanySearchSource}
          workspaceCreationFilters={workspaceCreationFilters}
          // Bulk search results are company-tab rows, so the backend treats them the same way.
          source={source === "company_bulk_search" ? "company_tab" : source}
          trackingSource={source}
        />
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <Modal size="md" centered show={showConfirmModal} onHide={handleCloseConfirmModal} animation={false} className="confirm-modal">
          <Modal.Header>
            <div className="w-100 d-flex">
              <div className="col-md-6 m-auto">
                <span className="header-confirm">Confirmation</span>
              </div>
              <div className="col-md-6 text-right pr-0">
                <button className="btn btn-primary trademo-new-btn mx-2 px-2" onClick={createExcelImporterExporter}>
                  Confirm
                </button>
                <button type="button" className="close trademo-modal-close" aria-label="Close" onClick={handleCloseConfirmModal}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <p className="modaltext mb-0">
              You are downloading list of <strong>{downloadCount}</strong> Prospect {type} which will cost you <strong>{creditCost} Download Credits.</strong>
            </p>
          </Modal.Body>
        </Modal>
      )}

      {/* Download Range Modal */}
      {showDownloadModal && (
        <Modal className="trademo-impexpmodal" centered show={showDownloadModal} onHide={handleCloseDownloadModal} animation={false}>
          <Modal.Header closeButton>
            <Modal.Title>Download Unique {searchedState?.tradeDirection?.value === "import" ? "Importers" : "Exporters"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row m-0">
              <div className="col-6 p-0">
                <ReactTooltip className="trademo-tooltip" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="bottom" />
                <span className="label-value line-height-32">
                  Select Unique {type} by S.No <Info className="ml-1 cursor-pointer" color="#8C8C8C" size={13} data-tip={`If selected count is more than ${DOWNLOAD_LIMIT}, shipments are emailed.`} />
                </span>
              </div>
              <div className="col-6 p-0">
                <div className="row m-0">
                  <div className="col-5 pr-0">
                    <input name="downloadFrom" className="download-input-field" placeholder="From" type="number" value={downloadFromValue} onChange={(e) => handleDownloadFromChange(e.target.value)} />
                  </div>
                  <div className="col-2">
                    <span className="label-value line-height-32">to</span>
                  </div>
                  <div className="col-5 pr-0">
                    <input name="downloadTo" className="download-input-field" placeholder="To" type="number" value={downloadToValue} onChange={(e) => handleDownloadToChange(e.target.value)} />
                  </div>
                </div>
                <div className="py-3">{!isDownloadRangeValid && downloadFromValue && downloadToValue ? <span className="error">*You can select maximum of 10000 at a time.</span> : <></>}</div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="col-8 p-0 m-0">{(!userFirstPlan?.user_active_plan?.includes("enterprise") || userFirstPlan?.is_free_trial === false) && <span className="download-credit-label m-auto">Note: Your download credits balance is {availableCredits}</span>}</div>
            <div className="col-4 p-0 m-0 d-flex justify-content-end">
              <button type="button" className="btn btn-outline-secondary trademo-outline-btn btn-sm mr-2" onClick={handleCloseDownloadModal}>
                Cancel
              </button>
              <button type="button" disabled={!isDownloadRangeValid} className="btn btn-primary btn-sm trademo-new-btn" onClick={showImporterConfirmModal}>
                {isEmailShipments ? "Email" : "Download"}
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      {showContactModal && <ContactModal show={showContactModal} prospectData={selectedRows} currentUserPlan={currentUserPlan} handleClose={() => setShowContactModal(false)} type={type} closeEventHandler={() => null} remainingValueAddCount={remainingValueAddCount} />}
    </>
  );
};

export default CompanyResultHeaders;
